/**
 * shadcn/ui Button (base-nova), with two local changes.
 *
 * 1. **Sizes are responsive to the POINTER, not to the call site.** Nova's
 *    scale is indoor mouse density (h-8/h-9). This app is also used one-handed
 *    outdoors, where WCAG 2.5.5 wants 44px. Rather than making every call site
 *    pick a size (which is how you end up with fifteen different buttons on
 *    one screen), each size grows on a coarse pointer. `size="default"` is
 *    36px with a mouse and 44px with a thumb, and a touchscreen laptop gets
 *    the big one, which is correct. Call sites just say `default` / `sm` /
 *    `lg` and never think about it again.
 *
 * 2. **`brand` / `go` / `stop` variants.** Nova's `default` is solid ink and
 *    stays the ordinary primary; `brand` is the pink one, reserved for brand
 *    and featured actions. `go` and `stop` are SEMANTIC ONLY (start, and
 *    stop-or-destroy) never decorative and never a second brand colour.
 *    Nova's own `destructive` is a tinted red for quiet row actions; `stop` is
 *    the solid one for "this ends something".
 *
 * 3. **The race slant.** Every standard button leans 6° (the owner picked the
 *    subtle variant of the overlay's plate geometry, 2026-09-01); the LABEL
 *    counter-skews upright, so type never tilts and the box keeps its exact
 *    height. The lean belongs to the OUTERMOST silhouette: inside a
 *    `[data-slant-host]` (ButtonGroup, the board's view toggle - anything
 *    that draws one shared slanted shell) the button's own skew turns off and
 *    the shell's carries it, so flush segments never open sawtooth seams and
 *    nothing leans twice. `slant={false}` is for a button EMBEDDED in a
 *    straight field row (the Stepper's -/+ inside its bordered input row),
 *    where a lone tilted glyph against a rectangular border reads as a bug.
 *    The slab stays straight always: it is a surface, not a control, and a
 *    skewed 200px slab displaces its edges into the neighbouring column.
 *
 * `outline` also fills with `bg-card` rather than `bg-background`: this app's
 * page ground IS `--background`, so nova's outline button was invisible on it.
 * Its edge is `--control-edge`, which is `--border` everywhere except the
 * scorekeeper's daylight ground (`.sun` in theme.css), where it is 4.5:1.
 */
import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        // `text-foreground` is NOT decoration: this variant paints its own
        // background, so it has to paint its own ink. Inheriting it put a
        // washed-out label on a white pill the moment one of these sat on the
        // dark band (the board's header), and the variant already forces
        // `hover:text-foreground`, so resting simply matches hover now.
        outline:
          "border-control-edge bg-card text-foreground shadow-xs hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20",
        link: "text-primary underline-offset-4 hover:underline",
        brand: "bg-brand text-brand-on hover:bg-brand/85 focus-visible:ring-brand/40",
        go: "bg-go text-go-foreground hover:bg-go/90 focus-visible:border-go focus-visible:ring-go/30",
        stop: "bg-stop text-stop-foreground hover:bg-stop/90 focus-visible:border-stop focus-visible:ring-stop/30",
      },
      size: {
        default:
          "h-9 gap-2 px-3 pointer-coarse:h-11 pointer-coarse:px-4 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5",
        sm: "h-8 gap-1.5 rounded-md px-2.5 text-[0.8rem] pointer-coarse:h-10 pointer-coarse:px-3 pointer-coarse:text-sm [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-10 gap-2 px-4 text-base pointer-coarse:h-12 pointer-coarse:px-5 [&_svg:not([class*='size-'])]:size-5",
        icon: "size-9 pointer-coarse:size-11",
        "icon-sm":
          "size-8 rounded-md pointer-coarse:size-10 [&_svg:not([class*='size-'])]:size-3.5",
        /**
         * The scorekeeper's primary score target. It fills whatever height the
         * phone has left; 112px is the FLOOR, not the design size, and it is
         * the one control in the app that is deliberately enormous.
         */
        slab:
          // The primary score target. It sizes ITSELF: a call site never
          // hands it `flex-1`, which is what turned it into 660px of chewing
          // gum on a tall desktop window. Scales with the viewport between a
          // 112px floor and a 208px ceiling, so a phone gets a thumb-sized
          // slab and a monitor gets a button.
          //
          // `svh`, not `dvh` (2026-09-14): the DYNAMIC viewport changes when
          // the browser's toolbar collapses on the first scroll, so the slab
          // grew 13-20px and every control below it slid under a thumb that
          // was already landing. The small viewport is fixed for the session.
          //
          // `data-edge` is the team colour's own call (`needsEdge`): a solid
          // inset ink edge when the painted fill is under 3:1 against the card
          // (a white or pale kit), nothing on a kit that already stands off
          // it. Keyboard focus is an OUTLINE offset from the slab, never a
          // ring, so a focused slab cannot look pressed or selected.
          "h-[clamp(var(--spacing-slab),24svh,13rem)] w-full flex-col gap-1 rounded-xl px-3 text-4xl font-bold tracking-tight motion-safe:active:scale-[0.98] data-[edge=true]:ring-2 data-[edge=true]:ring-inset data-[edge=true]:ring-foreground focus-visible:ring-0 focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring focus-visible:outline-solid",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  slant = true,
  children,
  ...props
}: ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    /** See the module doc: false only for a button embedded in a straight
     *  bordered field row, where the row's rectangle is the silhouette. */
    slant?: boolean;
  }) {
  const slanted = slant && size !== "slab";
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(
        buttonVariants({ variant, size, className }),
        // Inside a slant host the SHELL leans; the button goes straight in
        // the shell's own skewed space, and the label's +6 below then counters
        // exactly one lean either way.
        slanted && "-skew-x-6 in-data-[slant-host]:skew-x-0",
      )}
      {...props}
    >
      {/* `w-full min-w-0` + inherited justification: the wrapper must be
          invisible to layout, or a `truncate` child stops truncating and a
          `justify-start` button silently re-centres its content. */}
      {slanted ? (
        <span className="inline-flex w-full min-w-0 skew-x-6 items-center [justify-content:inherit] [gap:inherit]">
          {children}
        </span>
      ) : (
        children
      )}
    </ButtonPrimitive>
  );
}

export { Button, buttonVariants };
